import { useState, useEffect } from "react";
import ProjectInfoService, {
  ProjectInfoFields,
} from "../services/ProjectInfoService";

/**
 * Custom hook for managing CRREM scenarios
 *
 * @param {string} projectId - Project ID from URL parameters
 * @returns {Object} State and functions for CRREM scenario management
 */
const useCrremScenarios = (projectId) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedScenarios, setSelectedScenarios] = useState([]);

  const token = localStorage.getItem("token");
  const apiRoot = process.env.REACT_APP_API_ROOT;

  const projectInfoService = new ProjectInfoService(apiRoot, token);

  /**
   * Fetch scenarios from the server
   */
  const fetchScenarios = async () => {
    if (!projectId) {
      setError("Project ID is missing");
      setLoading(false);
      return { success: false, error: "Project ID is missing" };
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${apiRoot}/api/project_info/?project_id=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch scenarios");
      }

      const data = await response.json();
      let scenarioList = data[0]?.crrem_scenario_list;
      if (typeof scenarioList === "string") {
        scenarioList = JSON.parse(scenarioList);
      }
      setScenarios(Array.isArray(scenarioList) ? scenarioList : []);
    } catch (err) {
      console.error("Error fetching scenarios:", err);
      setError("Failed to load CRREM scenarios. Please try again.");
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }

    return { success: true };
  };

  useEffect(() => {
    fetchScenarios();
  }, [projectId]);

  /**
   * Handle scenario selection
   *
   * @param {string} scenarioId - ID of the scenario to select/deselect
   */
  const handleSelectScenario = (scenarioId) => {
    setSelectedScenarios((prev) => {
      if (prev.includes(scenarioId)) {
        return prev.filter((id) => id !== scenarioId);
      } else {
        return [...prev, scenarioId];
      }
    });
  };

  /**
   * Handle selection of all scenarios
   *
   * @param {Array} newSelectedScenarios - Array of scenario IDs to select
   */
  const handleSelectAllScenarios = (newSelectedScenarios) => {
    setSelectedScenarios(newSelectedScenarios);
  };

  /**
   * Update project info with new scenario list
   *
   * @param {Array} updatedScenarioList - Updated list of scenarios
   * @returns {Object} Result of the operation
   */
  const updateProjectScenarios = async (updatedScenarioList) => {
    try {
      const currentInfo = await projectInfoService.getProjectInfo(projectId);

      await projectInfoService.updateProjectInfo(projectId, {
        [ProjectInfoFields.CRREM_SCENARIO_LIST]:
          projectInfoService.stringifyJsonField(updatedScenarioList),
      });

      return { success: true };
    } catch (err) {
      console.error("Error updating project scenarios:", err);
      return {
        success: false,
        message: "Failed to update project scenarios",
      };
    }
  };

  /**
   * Delete selected scenarios
   *
   * @returns {Object} Result of the operation
   */
  const deleteScenarios = async () => {
    if (selectedScenarios.length === 0) {
      return { success: false, message: "No scenarios selected" };
    }

    try {
      setIsProcessing(true);

      for (const scenario of selectedScenarios) {
        const response = await fetch(
          `${apiRoot}/api/crrem_data/bulk_delete_by_degree/`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              project: projectId,
              degree: scenario,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail || errorData.error || "Failed to delete scenario"
          );
        }
      }

      const currentInfo = await projectInfoService.getProjectInfo(projectId);
      let scenarioList = projectInfoService.parseJsonField(
        currentInfo.crrem_scenario_list
      );

      scenarioList = scenarioList.filter((s) => !selectedScenarios.includes(s));

      await updateProjectScenarios(scenarioList);

      setScenarios(scenarioList);
      setSelectedScenarios([]);

      return {
        success: true,
        message: `Selected scenarios deleted successfully.`,
      };
    } catch (err) {
      console.error("Error deleting scenarios:", err);
      return {
        success: false,
        message: err.message || "Failed to delete scenarios",
      };
    } finally {
      setIsProcessing(false);
    }
  };
  /**
   * Upload a new scenario
   *
   * @param {string} scenarioName - Name of the new scenario
   * @param {File} file - Excel file with scenario data
   * @param {string} sheetName - Optional sheet name to use instead of default
   * @returns {Object} Result of the operation
   */
  const uploadScenario = async (scenarioName, file, sheetName = null) => {
    if (!scenarioName || !file) {
      return {
        success: false,
        message: "Please enter a scenario name and select a file.",
      };
    }

    try {
      setIsProcessing(true);

      if (scenarios.includes(scenarioName)) {
        return {
          success: false,
          message: `Scenario "${scenarioName}" already exists.`,
        };
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("project", projectId);
      formData.append("scenario_name", scenarioName);

      if (sheetName) {
        formData.append("sheet_name", sheetName);
      }

      const response = await fetch(`${apiRoot}/api/crrem_data/upload_excel/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || errorData.detail || "Failed to upload scenario"
        );
      }

      const data = await response.json();

      await projectInfoService.updateListField(
        projectId,
        ProjectInfoFields.CRREM_SCENARIO_LIST,
        scenarioName,
        { overwrite: false, unique: true }
      );

      await fetchScenarios();

      return {
        success: true,
        message: `Scenario "${scenarioName}" uploaded successfully. ${
          data.message || ""
        }`,
        data,
      };
    } catch (err) {
      console.error("Error uploading CRREM scenario:", err);
      return {
        success: false,
        message: err.message || "Error uploading scenario",
      };
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Rename a scenario
   *
   * @param {string} originalName - Original scenario name
   * @param {string} newName - New scenario name
   * @returns {Object} Result of the operation
   */
  const renameScenario = async (originalName, newName) => {
    if (!newName.trim()) {
      return {
        success: false,
        message: "Scenario name cannot be empty",
      };
    }

    try {
      setIsProcessing(true);

      if (scenarios.includes(newName)) {
        return {
          success: false,
          message: `Scenario "${newName}" already exists`,
        };
      }

      const response = await fetch(`${apiRoot}/api/crrem_data/update_degree/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          project: projectId,
          old_degree: originalName,
          new_degree: newName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to rename scenario");
      }

      const currentInfo = await projectInfoService.getProjectInfo(projectId);
      let currentScenarios = projectInfoService.parseJsonField(
        currentInfo.crrem_scenario_list
      );

      const updatedScenarios = currentScenarios.map((s) =>
        s === originalName ? newName : s
      );

      await projectInfoService.updateProjectInfo(projectId, {
        [ProjectInfoFields.CRREM_SCENARIO_LIST]:
          projectInfoService.stringifyJsonField(updatedScenarios),
      });

      await fetchScenarios();

      return {
        success: true,
        message: `Scenario renamed to "${newName}"`,
      };
    } catch (err) {
      console.error("Error renaming scenario:", err);
      return {
        success: false,
        message: err.message || "Failed to rename scenario",
      };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    scenarios,
    loading,
    isProcessing,
    error,
    selectedScenarios,

    fetchScenarios,
    handleSelectScenario,
    handleSelectAllScenarios,
    deleteScenarios,
    uploadScenario,
    renameScenario,
  };
};

export default useCrremScenarios;
